import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import './MatchSetup.css';

function MatchSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.state?.match;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock data for previously used statements - replace with actual API call
  const [previousStatements] = useState([
    "Team must score at least 2 goals",
    "Match must have over 2.5 goals",
    "Team must win by 2 or more goals",
    "Match must have at least 3 corners in first half",
    "Team must score in both halves"
  ]);

  const [questions, setQuestions] = useState([
    {
      id: 1,
      type: '4ka100',
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    }
  ]);

  const [parlayRules, setParlayRules] = useState([
    { id: 1, statement: '', odds: 0 }
  ]);

  // Initialize with existing questions and parlay rules if they exist
  useEffect(() => {
    if (match) {
      // Initialize questions if they exist
      if (match.questions && match.questions.length > 0) {
        setQuestions(match.questions.map((q, index) => ({
          ...q,
          id: index + 1
        })));
      }

      // Initialize parlay rules if they exist
      if (match.parlayRules && match.parlayRules.length > 0) {
        setParlayRules(match.parlayRules.map((r, index) => ({
          ...r,
          id: index + 1
        })));
      }
    }
  }, [match]);

  const handleQuestionChange = (questionId, field, value) => {
    setQuestions(prev => prev.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ));
  };

  const handleOptionChange = (questionId, optionIndex, value) => {
    setQuestions(prev => prev.map(q => 
      q.id === questionId ? {
        ...q,
        options: q.options.map((opt, idx) => idx === optionIndex ? value : opt)
      } : q
    ));
  };

  const handleParlayRuleChange = (id, field, value) => {
    setParlayRules(rules =>
      rules.map(rule => {
        if (rule.id === id) {
          // If the field is odds, convert it to a number if possible
          if (field === 'odds') {
            const numValue = value === '' ? 0 : parseFloat(value);
            return { ...rule, [field]: numValue };
          }
          return { ...rule, [field]: value };
        }
        return rule;
      })
    );
  };

  const handleToggleDropdown = (ruleId) => {
    setParlayRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, showDropdown: !r.showDropdown } : r
    ));
  };

  const handleSelectStatement = (ruleId, statement) => {
    setParlayRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, statement, showDropdown: false } : r
    ));
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      id: prev.length + 1,
      type: '4ka100',
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    }]);
  };

  const addParlayRule = () => {
    setParlayRules(prev => [...prev, {
      id: prev.length + 1,
      statement: '',
      odds: 0
    }]);
  };

  const handleDeleteQuestion = (questionId) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const handleDeleteParlayRule = (ruleId) => {
    setParlayRules(prev => prev.filter(r => r.id !== ruleId));
  };

  const isFormValid = () => {
    // Check if all questions are filled
    const questionsValid = questions.every(q => 
      q.question.trim() !== '' && 
      q.options.every(opt => opt.trim() !== '') &&
      q.correctAnswer !== null
    );

    // Check if all parlay rules are filled
    const parlayRulesValid = parlayRules.every(r => {
      // Handle the case where odds could be a number or string
      const oddsValue = r.odds;
      const oddsIsValid = oddsValue !== undefined && 
                         oddsValue !== null && 
                         oddsValue !== '' &&
                         !isNaN(parseFloat(oddsValue)) && 
                         parseFloat(oddsValue) > 0;
      
      return r.statement.trim() !== '' && oddsIsValid;
    });

    return questionsValid && parlayRulesValid;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Show loading toast
    const loadingToastId = toast.loading("Saving match setup...");
    
    try {
      // Check that the match data exists
      if (!match) {
        throw new Error('Match data is missing or invalid');
      }

      // Get matchId from either match.matchId or match._id
      const matchId = match.matchId || match._id;
      
      console.log('Using match ID for submission:', matchId);
      console.log('Complete match object:', match);

      // Prepare data for API using correct property names from the match object
      const setupData = {
        matchId: matchId ? matchId.toString() : Date.now().toString(), // Use matchId or generate new one
        _id: match._id, // Include the MongoDB _id to help with identifying existing records
        team1: match.team1,
        team2: match.team2,
        matchDate: match.matchDate,        // Use matchDate
        matchTime: match.matchTime,        // Use matchTime
        status: match.status || 'upcoming',
        questions: questions.map(q => ({
          type: q.type,
          question: q.question.trim(),
          options: q.options.map(opt => opt.trim()),
          correctAnswer: parseInt(q.correctAnswer)
        })),
        parlayRules: parlayRules.map(r => ({
          statement: r.statement.trim(),
          odds: parseFloat(r.odds) // Convert odds to number
        }))
      };

      console.log('Sending match setup data:', setupData);

      // Make API call
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
          // Removed X-Match-ID header to avoid CORS issues
        },
        body: JSON.stringify(setupData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to save match setup');
      }

      console.log('Match setup response:', result);
      
      // Update toast with success message based on response
      const successMessage = result.message || "Match setup saved successfully!";
      
      toast.update(loadingToastId, { 
        render: `${successMessage} Redirecting to dashboard...`, 
        type: "success", 
        isLoading: false,
        autoClose: 2000
      });

      // Wait for 2 seconds before navigating
      setTimeout(() => {
        // Navigate back to dashboard
        navigate('/admin/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error saving match setup:', error);
      
      // Update toast with error message
      toast.update(loadingToastId, { 
        render: `Failed to save match setup: ${error.message || 'Unknown error'}`, 
        type: "error", 
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!match) {
    return (
      <div className="match-setup-error">
        <h2>No match selected</h2>
        <button onClick={() => navigate('/admin/dashboard')}>Go Back</button>
      </div>
    );
  }

  // Debugging match data
  console.log('Match data received:', match);

  return (
    <div className="match-setup">
      <header className="setup-header">
        <h1>Match Setup: {match.team1} vs {match.team2}</h1>
        {/* <h1>{match.date}</h1> 
        <h1>{match.time}</h1> */}
        <button onClick={() => navigate('/admin/dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      <main className="setup-content">
        <section className="questions-section">
          <div className="section-header">
            <h2>4ka100 Questions</h2>
            <button onClick={addQuestion} className="add-button">
              Add Question
            </button>
          </div>

          {questions.map((q, index) => (
            <div key={q.id} className="question-card">
              <div className="question-header">
                <h3>Question {index + 1}</h3>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteQuestion(q.id)}
                  title="Delete question"
                >
                  ×
                </button>
              </div>
              <div className="question-content">
                <div className="form-group">
                  <label>Question Text</label>
                  <input
                    type="text"
                    value={q.question}
                    onChange={(e) => handleQuestionChange(q.id, 'question', e.target.value)}
                    placeholder="Enter question text"
                  />
                </div>
                <div className="options-group">
                  <label>Options</label>
                  {q.options.map((option, optIndex) => (
                    <div key={optIndex} className="option-input">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(q.id, optIndex, e.target.value)}
                        placeholder={`Option ${optIndex + 1}`}
                      />
                      <input
                        type="radio"
                        name={`correct-${q.id}`}
                        checked={q.correctAnswer === optIndex}
                        onChange={() => handleQuestionChange(q.id, 'correctAnswer', optIndex)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="parlay-section">
          <div className="section-header">
            <h2>Parlay Rules</h2>
            <button onClick={addParlayRule} className="add-button">
              Add Rule
            </button>
          </div>

          {parlayRules.map(rule => (
            <div key={rule.id} className="parlay-card">
              <div className="parlay-header">
                <h3>Rule {rule.id}</h3>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteParlayRule(rule.id)}
                  title="Delete rule"
                >
                  ×
                </button>
              </div>
              <div className="form-group">
                <label>Statement</label>
                <div className="statement-input-container">
                  <input
                    type="text"
                    value={rule.statement}
                    onChange={(e) => handleParlayRuleChange(rule.id, 'statement', e.target.value)}
                    placeholder="Enter parlay rule statement"
                  />
                  <button 
                    className="dropdown-toggle"
                    onClick={() => handleToggleDropdown(rule.id)}
                  >
                    ▼
                  </button>
                  {rule.showDropdown && (
                    <div className="statement-dropdown">
                      {previousStatements.map((statement, index) => (
                        <div
                          key={index}
                          className="statement-option"
                          onClick={() => handleSelectStatement(rule.id, statement)}
                        >
                          {statement}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Odds</label>
                <input
                  type="number"
                  value={rule.odds || ''}
                  onChange={(e) => handleParlayRuleChange(rule.id, 'odds', e.target.value)}
                  placeholder="Enter odds (e.g., 1.5)"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
          ))}
        </section>

        <div className="submit-section">
          <button 
            onClick={handleSubmit} 
            className="submit-button"
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Match Setup'}
          </button>
        </div>
      </main>
    </div>
  );
}

export default MatchSetup; 